export interface HelperDefinition {
  name: string;
  description: string;
  documentation: string;
  signature: string;
  syntax: string;
  examples: string[];
  parameterType?: 'metric' | 'format' | 'entity' | 'none';
}

export const HELPER_REGISTRY: Record<string, HelperDefinition> = {
  SUM: {
    name: 'SUM',
    description: 'Sum of all values',
    documentation: 'Sum of all values.',
    signature: '{{SUM "headcount"}}',
    syntax: '{{SUM "metric_key"}}',
    examples: ['{{SUM "headcount"}}', '{{SUM "salary" segment="Engineering"}}'],
    parameterType: 'metric',
  },
  AVG: {
    name: 'AVG',
    description: 'Average of all values',
    documentation: 'Average of all values.',
    signature: '{{AVG "tenure"}}',
    syntax: '{{AVG "metric_key"}}',
    examples: [
      '{{AVG "tenure"}}',
      '{{AVG "salary" entity=(FILTER "COMPANY")}}',
    ],
    parameterType: 'metric',
  },
  MIN: {
    name: 'MIN',
    description: 'Minimum value',
    documentation:
      'Minimum value from a metric, or minimum of numeric arguments.',
    signature: '{{MIN "base_salary"}}',
    syntax: '{{MIN "metric_key"}} | {{MIN number_a number_b ...}}',
    examples: ['{{MIN "base_salary"}}', '{{MIN (SUM "x") (SUM "y")}}'],
    parameterType: 'metric',
  },
  MAX: {
    name: 'MAX',
    description: 'Maximum value',
    documentation:
      'Maximum value from a metric, or maximum of numeric arguments.',
    signature: '{{MAX "base_salary"}}',
    syntax: '{{MAX "metric_key"}} | {{MAX number_a number_b ...}}',
    examples: ['{{MAX "base_salary"}}', '{{MAX (SUM "x") (SUM "y")}}'],
    parameterType: 'metric',
  },
  COUNT: {
    name: 'COUNT',
    description: 'Count of data points',
    documentation: 'Count of data points.',
    signature: '{{COUNT "headcount"}}',
    syntax: '{{COUNT "metric_key"}}',
    examples: [
      '{{COUNT "headcount"}}',
      '{{COUNT "overall" segment="Finance"}}',
    ],
    parameterType: 'metric',
  },
  VALUE: {
    name: 'VALUE',
    description: 'Extract a single value',
    documentation:
      'Extract a single value with selectors like entity, segment, and label. For composition breakdowns use secondaryDimension="..." (e.g. ethnicity, gender). segment = breakdown category (e.g. Female, White); entity = outer dimension (e.g. geography/company name). For composition data you must pass segment and entity—otherwise VALUE returns empty. property="share" returns the pre-computed share (0..1) for BREAKDOWN_SINGLE.',
    signature: '{{VALUE "headcount" entity="Workday"}}',
    syntax:
      '{{VALUE "metric_key" [secondaryDimension="..."] [entity="..."] [segment="..."] [property="value|share"]}}',
    examples: [
      '{{VALUE "headcount" entity="Workday"}}',
      '{{VALUE "salary" segment="Engineering"}}',
      '{{VALUE "headcount" secondaryDimension="ethnicity" segment="White" entity=(PARAM "Target Geography")}}',
      '{{VALUE "headcount" secondaryDimension="ethnicity" segment="White" entity=(PARAM "Company") property="share"}}',
    ],
    parameterType: 'metric',
  },
  HAS_DATA: {
    name: 'HAS_DATA',
    description: 'Check if a metric has data',
    documentation: 'Returns true/false based on whether the metric has data.',
    signature: '{{#if (HAS_DATA "headcount")}}...{{/if}}',
    syntax: '{{HAS_DATA "metric_key"}}',
    examples: [
      '{{#if (HAS_DATA "headcount")}}Has data{{/if}}',
      '{{HAS_DATA "salary" segment="Engineering"}}',
    ],
    parameterType: 'metric',
  },
  ARG_MAX: {
    name: 'ARG_MAX',
    description: 'Entity label with maximum value',
    documentation:
      'Returns the label/entity with the maximum value. Default reduce is last (latest value). ',
    signature: '{{ARG_MAX "headcount"}}',
    syntax: '{{ARG_MAX "metric_key"}}',
    examples: [
      '{{ARG_MAX "headcount"}}',
      '{{ARG_MAX "headcount" reduce="avg"}}',
    ],
    parameterType: 'metric',
  },
  ARG_MIN: {
    name: 'ARG_MIN',
    description: 'Entity label with minimum value (by last/latest by default)',
    documentation:
      'Returns the label/entity with the minimum value. Default reduce is last (latest value).',
    signature: '{{ARG_MIN "headcount"}}',
    syntax: '{{ARG_MIN "metric_key"}}',
    examples: [
      '{{ARG_MIN "headcount"}}',
      '{{ARG_MIN "headcount" reduce="avg"}}',
    ],
    parameterType: 'metric',
  },
  TABLE: {
    name: 'TABLE',
    description: 'Markdown table of labels and values for a metric',
    documentation:
      'Renders a markdown table of entity labels and values, sorted by value descending. For composition breakdowns use secondaryDimension="...". Optional: format (e.g. SI, PERCENTAGE), reduce (sum, avg), limit (max rows). For composition, segment filters by outer dimension (entity), e.g. one geography or company.',
    signature: '{{TABLE "headcount"}}',
    syntax:
      '{{TABLE "metric_key" [secondaryDimension="..."] [reduce="sum|avg|min|max|first|last|median"] [format="SI"] [limit="10"] [segment="..."]}}',
    examples: [
      '{{TABLE "headcount"}}',
      '{{TABLE "headcount" reduce="avg"}}',
      '{{TABLE "headcount" format="$" limit="5"}}',
      '{{TABLE "headcount" secondaryDimension="ethnicity" segment=(PARAM "Company") format="SI"}}',
      '{{TABLE "headcount" secondaryDimension="gender" format="PERCENTAGE"}}',
    ],
    parameterType: 'metric',
  },
  RANK: {
    name: 'RANK',
    description: '1-based rank of an entity',
    documentation: 'Returns rank for an entity on a metric.',
    signature: '{{RANK "headcount" (FILTER "COMPANY")}}',
    syntax: '{{RANK "metric_key" entity="entity_name"}}',
    examples: [
      '{{RANK "headcount" entity="Workday"}}',
      '{{RANK "headcount" (FILTER "COMPANY")}}',
    ],
    parameterType: 'metric',
  },
  PERCENTILE_RANK: {
    name: 'PERCENTILE_RANK',
    description: 'Percentile rank (0..1) of an entity',
    documentation: 'Percentile rank (0..1) across one or more metrics.',
    signature: '{{PERCENTILE_RANK "overall" entity=(FILTER "COMPANY")}}',
    syntax:
      '{{PERCENTILE_RANK "metric_key" [additional_metric_keys...] entity="entity_name"}}',
    examples: [
      '{{PERCENTILE_RANK "overall" entity=(FILTER "COMPANY")}}',
      '{{PERCENTILE_RANK "m1" "m2" entity="Workday"}}',
    ],
    parameterType: 'metric',
  },
  SHARE: {
    name: 'SHARE',
    description: 'Share (ratio) of entity to total',
    documentation:
      'Share value from 0..1 for selected entity over total. For composition breakdowns use secondaryDimension="..." with segment (breakdown category, e.g. Female, White) and entity (outer dimension, e.g. geography/company).',
    signature:
      '{{FORMAT (SHARE "headcount" entity=(FILTER "COMPANY")) format="PERCENTAGE"}}',
    syntax:
      '{{SHARE "metric_key" [secondaryDimension="..."] [entity="..."] [segment="..."]}}',
    examples: [
      '{{SHARE "headcount" entity=(FILTER "COMPANY")}}',
      '{{FORMAT (SHARE "headcount" entity="Workday") format="PERCENTAGE"}}',
      '{{SHARE "headcount" secondaryDimension="gender" segment="Female" entity=(PARAM "Company")}}',
    ],
    parameterType: 'metric',
  },
  MAX_POINT: {
    name: 'MAX_POINT',
    description: 'Max point in a time series',
    documentation:
      'Returns date/value/entity for the maximum point in a time series.',
    signature: '{{MAX_POINT "overall" (FILTER "COMPANY") property="date"}}',
    syntax: '{{MAX_POINT "metric_key" [entity] property="date|value|entity"}}',
    examples: [
      '{{MAX_POINT "overall" property="date"}}',
      '{{MAX_POINT "overall" (FILTER "COMPANY") property="value"}}',
    ],
    parameterType: 'metric',
  },
  MIN_POINT: {
    name: 'MIN_POINT',
    description: 'Min point in a time series',
    documentation:
      'Returns date/value/entity for the minimum point in a time series.',
    signature: '{{MIN_POINT "overall" (FILTER "COMPANY") property="date"}}',
    syntax: '{{MIN_POINT "metric_key" [entity] property="date|value|entity"}}',
    examples: [
      '{{MIN_POINT "overall" property="date"}}',
      '{{MIN_POINT "overall" (FILTER "COMPANY") property="value"}}',
    ],
    parameterType: 'metric',
  },
  FORMAT: {
    name: 'FORMAT',
    description: 'Format a number or date',
    documentation:
      'Formats helper output using format keys like SI, PERCENTAGE, DATE, and ORDINAL.',
    signature: '{{FORMAT (SUM "headcount") format="SI"}}',
    syntax: '{{FORMAT value format="SI|PERCENTAGE|DATE|ORDINAL|$"}}',
    examples: [
      '{{FORMAT (SUM "headcount") format="SI"}}',
      '{{FORMAT (RANK "overall" entity="Workday") format="ORDINAL"}}',
    ],
    parameterType: 'format',
  },
  FILTER: {
    name: 'FILTER',
    description: 'Render a dashboard filter value',
    documentation: 'Renders a dashboard filter value by filter ID.',
    signature: '{{FILTER "COMPANY"}}',
    syntax: '{{FILTER "FILTER_ID"}}',
    examples: ['{{FILTER "COMPANY"}}', '{{FILTER "GEOGRAPHY"}}'],
    parameterType: 'none',
  },
  PARAM: {
    name: 'PARAM',
    description: 'Access a report parameter by name',
    documentation:
      'Returns the value of a report parameter by its name (reportParameters[].name).',
    signature: '{{PARAM "Company"}}',
    syntax: '{{PARAM "parameterName"}}',
    examples: ['{{PARAM "Company"}}', '{{PARAM "Competitors"}}'],
    parameterType: 'none',
  },
  HAS_PARAM: {
    name: 'HAS_PARAM',
    description: 'Whether a report parameter has a non-empty display value',
    documentation:
      'Returns true when the named report parameter exists and has at least one selection (non-empty formatted value). Use in {{#if (HAS_PARAM "Occupation")}} for optional parameters.',
    signature: '{{HAS_PARAM "Occupation"}}',
    syntax: '{{HAS_PARAM "parameterName"}}',
    examples: [
      '{{#if (HAS_PARAM "Occupation")}} for {{PARAM "Occupation"}}{{/if}}',
      '{{HAS_PARAM "Company"}}',
    ],
    parameterType: 'none',
  },
  METRIC: {
    name: 'METRIC',
    description: 'Current selected metric display title',
    documentation: 'Returns the current selected metric display title.',
    signature: '{{METRIC}}',
    syntax: '{{METRIC}}',
    examples: ['{{METRIC}}'],
    parameterType: 'none',
  },
  ADD: {
    name: 'ADD',
    description: 'Add numbers',
    documentation: 'Adds numeric arguments.',
    signature: '{{ADD (SUM "x") (SUM "y")}}',
    syntax: '{{ADD value_a value_b ...}}',
    examples: ['{{ADD 10 20}}', '{{ADD (SUM "x") (SUM "y")}}'],
    parameterType: 'none',
  },
  SUBTRACT: {
    name: 'SUBTRACT',
    description: 'Subtract b from a',
    documentation: 'Subtracts the second numeric value from the first.',
    signature: '{{SUBTRACT (SUM "x") (SUM "y")}}',
    syntax: '{{SUBTRACT value_a value_b}}',
    examples: [
      '{{SUBTRACT 20 5}}',
      '{{SUBTRACT (SUM "revenue") (SUM "cost")}}',
    ],
    parameterType: 'none',
  },
  MULTIPLY: {
    name: 'MULTIPLY',
    description: 'Multiply numbers',
    documentation: 'Multiplies numeric arguments.',
    signature: '{{MULTIPLY (AVG "x") 100}}',
    syntax: '{{MULTIPLY value_a value_b ...}}',
    examples: ['{{MULTIPLY 2 3}}', '{{MULTIPLY (AVG "x") 100}}'],
    parameterType: 'none',
  },
  DIVIDE: {
    name: 'DIVIDE',
    description: 'Divide a by b',
    documentation: 'Divides the first numeric value by the second.',
    signature: '{{DIVIDE (SUM "x") (COUNT "y")}}',
    syntax: '{{DIVIDE value_a value_b}}',
    examples: ['{{DIVIDE 10 2}}', '{{DIVIDE (SUM "x") (COUNT "y")}}'],
    parameterType: 'none',
  },
  MEAN: {
    name: 'MEAN',
    description: 'Average of numeric arguments',
    documentation: 'Mean of numeric arguments.',
    signature: '{{MEAN (SUM "q1") (SUM "q2")}}',
    syntax: '{{MEAN value_a value_b ...}}',
    examples: ['{{MEAN 1 2 3}}', '{{MEAN (SUM "q1") (SUM "q2")}}'],
    parameterType: 'none',
  },
  MATH: {
    name: 'MATH',
    description: 'Evaluate a math expression',
    documentation:
      'Evaluates an expression string with hash variables provided as numbers.',
    signature: '{{MATH "x / y * 100" x=(SUM "salary") y=(COUNT "headcount")}}',
    syntax: '{{MATH "expression" x=1 y=2 ...}}',
    examples: [
      '{{MATH "x / y * 100" x=(SUM "salary") y=(COUNT "headcount")}}',
      '{{MATH "(a + b) / 2" a=10 b=20}}',
    ],
    parameterType: 'none',
  },
  LIST: {
    name: 'LIST',
    description: 'Natural language list of entity labels',
    documentation:
      'Returns entity labels as a natural language list ("A, B, and C"). Entities are sorted by value descending. Use limit to cap the number of items.',
    signature: '{{LIST "target_companies"}}',
    syntax:
      '{{LIST "metric_key" [limit="3"] [reduce="sum|avg|last"] [sort="desc|asc"]}}',
    examples: [
      '{{LIST "target_companies"}}',
      '{{LIST "headcount" limit="3"}}',
      '{{LIST "headcount" limit="5" reduce="avg" sort="asc"}}',
    ],
    parameterType: 'metric',
  },
  SKILLS_EXPLANATION: {
    name: 'SKILLS_EXPLANATION',
    description: 'Skills summary paragraph from breakdown data',
    documentation:
      'Computes top skill, biggest deficit, and biggest surplus from breakdown data and returns a full paragraph. Hardwired to the market-conditions report filter names.',
    signature: '{{SKILLS_EXPLANATION "sq:saved-query-id"}}',
    syntax: '{{SKILLS_EXPLANATION "metric_key"}}',
    examples: [
      '{{SKILLS_EXPLANATION "sq:9897262d-84e8-457a-8fcf-55e4abb5f2d3"}}',
    ],
    parameterType: 'metric',
  },
};

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const ALL_HELPERS_REGEX = new RegExp(
  `\\b(?:${Object.keys(HELPER_REGISTRY).map(escapeRegex).join('|')})\\b`,
  'i'
);

export function getHelperDefinition(
  name: string
): HelperDefinition | undefined {
  const lookup = name.toLowerCase();
  return Object.values(HELPER_REGISTRY).find(
    (helper) => helper.name.toLowerCase() === lookup
  );
}
